import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { logo } from '../../../Data/Images/Home';
import CustomLink from '../../CustomLink';
import { useLazyLookupReviewerQuery, useSubmitReviewMutation } from '../../../features/reviews/reviewsApiSlice';
import './reviews.css';

// ─────────────────────────────────────────────────────────────────────────
// /reviews?email=... — standalone review collection page. The link is just
// the customer's email (no token to mint), so it can be personalized
// straight from an ESP merge tag, e.g. Omnisend's {{contact.email}}.
// Landing here without an ?email= (or with one that already submitted)
// just shows an editable email field at the top of the same form — no
// separate gating step. Trustpilot / reviews.io style. See riise-server
// controllers/reviews.controller.js + services/review.service.js.
// ─────────────────────────────────────────────────────────────────────────

const RATING_LABEL_KEYS = ['Reviews_Rating1', 'Reviews_Rating2', 'Reviews_Rating3', 'Reviews_Rating4', 'Reviews_Rating5'];

const Star = ({ filled, size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill={filled ? 'var(--riise-butter)' : '#E7E7DB'}>
    <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
  </svg>
);

const StarPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;

  return (
    <div className="riise-reviews-star-picker">
      <div className="riise-reviews-star-row" role="radiogroup" aria-label={t('Reviews_RatingLabel')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            className="riise-reviews-star-btn"
            aria-label={t(RATING_LABEL_KEYS[n - 1])}
            aria-checked={value === n}
            role="radio"
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(n)}
          >
            <Star filled={n <= display} />
          </button>
        ))}
      </div>
      {display > 0 && <div className="riise-reviews-star-caption">{t(RATING_LABEL_KEYS[display - 1])}</div>}
    </div>
  );
};

const ReviewsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // ?t= is the signed link an Omnisend campaign builds from the reviewToken
  // merge tag. ?email= is the older shape, kept working for links already sent
  // and for forwarded ones — but those submissions are recorded as unverified,
  // and the address is visible in the URL (so it reaches the Meta pixel and
  // Contentsquare), which is exactly what the token form avoids.
  const tokenFromUrl = searchParams.get('t');
  const emailFromUrl = searchParams.get('email');
  // The signed-in dashboard hands the address over in router state so it never
  // appears in the URL at all.
  const location = useLocation();
  const linkEmail = emailFromUrl || location.state?.email || null;
  const hasLinkParam = Boolean(tokenFromUrl || linkEmail);

  useEffect(() => {
    if (!tokenFromUrl) return;
    const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }, [tokenFromUrl]);

  const [
    lookupReviewer,
    { data: lookupData, isFetching: isLookingUp, isUninitialized: lookupNotStarted, isError: lookupFailed },
  ] = useLazyLookupReviewerQuery();
  const [submitReview, { isLoading: isSubmitting }] = useSubmitReviewMutation();

  const [rating, setRating] = useState(0);
  const [emailInput, setEmailInput] = useState('');
  const [problemsText, setProblemsText] = useState('');
  const [purchaseReasonText, setPurchaseReasonText] = useState('');
  const [improveText, setImproveText] = useState('');
  const [contactOk, setContactOk] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (tokenFromUrl) lookupReviewer({ token: tokenFromUrl });
    else if (linkEmail) lookupReviewer({ email: linkEmail });
  }, [tokenFromUrl, linkEmail, lookupReviewer]);

  // If the lookup can't complete we still know the address from an ?email=
  // link, so seed the form with it rather than making the customer retype it.
  // A ?t= link has nothing to fall back on by design — the address isn't in
  // the URL — so those visitors get the email field instead.
  useEffect(() => {
    if (lookupFailed && linkEmail) setEmailInput((current) => current || linkEmail);
  }, [lookupFailed, linkEmail]);

  // `lookupNotStarted` matters: the lazy query only fires in the effect above,
  // so on the very first paint isLookingUp is still false. Without it the page
  // briefly renders the "which email did you order with?" field to someone who
  // arrived with a link already identifying them.
  const isEmailPending = hasLinkParam && (lookupNotStarted || isLookingUp);
  const hasKnownEmail = hasLinkParam && !isEmailPending && Boolean(lookupData);
  const alreadySubmitted = (hasKnownEmail && lookupData.already_submitted) || duplicate;
  const hasInvalidToken = Boolean(tokenFromUrl) && lookupFailed;

  const activeEmail = hasKnownEmail ? lookupData.email : emailInput.trim();
  const canSubmit = rating > 0 && contactOk !== null && !isSubmitting && activeEmail.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setErrorMessage('');
    try {
      await submitReview({
        // Sent when present; the server prefers it over the address and only
        // marks a review verified when it resolves.
        token: tokenFromUrl || undefined,
        email: activeEmail,
        rating,
        problems_text: problemsText,
        purchase_reason_text: purchaseReasonText,
        improve_text: improveText,
        contact_ok: contactOk,
        language_code: (localStorage.getItem('lang') || 'de').toLowerCase(),
      }).unwrap();
      setSubmitted(true);
    } catch (err) {
      // Server messages are English-only, so they can't be rendered on a de/fr
      // page. Map the status to a translated string instead of echoing them.
      if (err?.status === 409) {
        setDuplicate(true);
        return;
      }
      setErrorMessage(err?.status === 400 ? t('Reviews_SubmitInvalid') : t('Reviews_SubmitError'));
    }
  };

  return (
    <div className="riise-reviews-page">
      <div className="riise-reviews-container">
        <div className="riise-reviews-header">
          <CustomLink to="/">
            <img src={logo} alt="riise." className="riise-reviews-logo" />
          </CustomLink>
        </div>

        {isEmailPending && (
          <div className="riise-reviews-status-card">
            <p className="riise-reviews-status-body">{t('Reviews_Loading')}</p>
          </div>
        )}

        {!isEmailPending && alreadySubmitted && (
          <div className="riise-reviews-status-card riise-reviews-thankyou">
            <div className="riise-reviews-status-title">{t('Reviews_AlreadySubmittedTitle')}</div>
            <p className="riise-reviews-status-body">{t('Reviews_AlreadySubmittedBody')}</p>
          </div>
        )}

        {!isEmailPending && !alreadySubmitted && !submitted && (
          <>
            <div className="riise-reviews-intro">
              <div className="riise-reviews-kicker">{t('Reviews_Kicker')}</div>
              <h1 className="riise-reviews-title">
                {hasKnownEmail && lookupData.name
                  ? t('Reviews_TitleWithName', { name: lookupData.name })
                  : t('Reviews_Title')}
              </h1>
              <p className="riise-reviews-subtitle">{t('Reviews_Subtitle')}</p>
            </div>

            <form className="riise-reviews-form" onSubmit={handleSubmit}>
              {hasInvalidToken && <div className="riise-reviews-error">{t('Reviews_InvalidLink')}</div>}
              {!hasKnownEmail && (
                <div className="riise-reviews-card">
                  <div className="riise-reviews-question">{t('Reviews_Q_Email')}</div>
                  <input
                    type="email"
                    required
                    className="riise-reviews-textarea riise-reviews-email-input"
                    placeholder={t('Reviews_EmailPlaceholder')}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                  />
                </div>
              )}

              <div className="riise-reviews-card riise-reviews-rating-card">
                <div className="riise-reviews-question">{t('Reviews_Q_Rating')}</div>
                <StarPicker value={rating} onChange={setRating} />
              </div>

              <div className="riise-reviews-card">
                <div className="riise-reviews-question">{t('Reviews_Q_Problems')}</div>
                <textarea
                  className="riise-reviews-textarea"
                  rows={3}
                  value={problemsText}
                  onChange={(e) => setProblemsText(e.target.value)}
                  placeholder={t('Reviews_AnswerPlaceholder')}
                />
              </div>

              <div className="riise-reviews-card">
                <div className="riise-reviews-question">{t('Reviews_Q_Why')}</div>
                <textarea
                  className="riise-reviews-textarea"
                  rows={3}
                  value={purchaseReasonText}
                  onChange={(e) => setPurchaseReasonText(e.target.value)}
                  placeholder={t('Reviews_AnswerPlaceholder')}
                />
              </div>

              <div className="riise-reviews-card">
                <div className="riise-reviews-question">{t('Reviews_Q_Improve')}</div>
                <textarea
                  className="riise-reviews-textarea"
                  rows={3}
                  value={improveText}
                  onChange={(e) => setImproveText(e.target.value)}
                  placeholder={t('Reviews_AnswerPlaceholder')}
                />
              </div>

              <div className="riise-reviews-card">
                <div className="riise-reviews-question">{t('Reviews_Q_Contact')}</div>
                <div className="riise-reviews-radio-row">
                  <button
                    type="button"
                    className={`riise-reviews-radio-pill ${contactOk === true ? 'is-selected' : ''}`}
                    aria-pressed={contactOk === true}
                    onClick={() => setContactOk(true)}
                  >
                    {t('Reviews_Yes')}
                  </button>
                  <button
                    type="button"
                    className={`riise-reviews-radio-pill ${contactOk === false ? 'is-selected' : ''}`}
                    aria-pressed={contactOk === false}
                    onClick={() => setContactOk(false)}
                  >
                    {t('Reviews_No')}
                  </button>
                </div>
              </div>

              {errorMessage && <div className="riise-reviews-error">{errorMessage}</div>}

              <button type="submit" className="riise-reviews-submit-btn" disabled={!canSubmit}>
                {isSubmitting ? t('Reviews_Submitting') : t('Reviews_Submit')}
              </button>
            </form>
          </>
        )}

        {submitted && (
          <div className="riise-reviews-status-card riise-reviews-thankyou">
            <div className="riise-reviews-status-title">{t('Reviews_ThankYouTitle')}</div>
            <p className="riise-reviews-status-body">{t('Reviews_ThankYouBody')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;
